import { type FC } from "react";
import { useParams } from "react-router-dom";
import {
    useGetWorkflow,
    useGetWorkflowExecution,
    useListWorkflowExecutionActions,
    useListWorkflowExecutionOutputs,
    type PlainWorkflowExecution,
} from "@/queries/automation-queries";
import { workflowName } from "@/utils/automations";
import { ErrorMessage } from "@/components/ErrorMessage";
import { WorkflowExecutionSummary } from "@/components/automations/AutomationExecutionSummary";
import { WorkflowExecutionPerformanceChart } from "@/components/automations/AutomationExecutionPerformanceChart";
import { WorkflowExecutionOutputsTable } from "@/components/automations/WorkflowExecutionOutputsTable";
import { Banner } from "@/components/flexkit/Banner";
import { Text } from "@/components/podkit/typography/Text";
import { IconSpinner } from "@/assets/icons/geist/IconSpinner";
import { IconTrash } from "@/assets/icons/geist/IconTrash";
import { IconWarningCircle } from "@/assets/icons/geist/IconWarningCircle";
import { IconCircleCheckedFilled } from "@/assets/icons/central/IconCircleCheckedFilled";
import { WorkflowExecutionPhase } from "gitpod-next-api/gitpod/v1/workflow_pb";

export const AutomationExecutionSummaryPage: FC = () => {
    const { workflowExecutionId } = useParams<{ workflowExecutionId: string }>();
    const { data: workflowExecution, error: workflowExecutionError } = useGetWorkflowExecution(workflowExecutionId);
    const { data: workflow } = useGetWorkflow(workflowExecution?.metadata?.workflowId);
    const { data: actions } = useListWorkflowExecutionActions({
        enabled: !!workflowExecutionId,
        workflowExecutionId: workflowExecutionId || "",
    });
    const { data: outputs } = useListWorkflowExecutionOutputs({
        enabled: !!workflowExecutionId,
        workflowExecutionId: workflowExecutionId || "",
    });

    return (
        <div data-testid="workflow-execution-summary" className="flex size-full flex-col gap-4">
            {workflowExecution && workflow && (
                <div className="flex flex-col gap-1">
                    <div className="flex items-center gap-2">
                        <ExecutionStatusIconLarge execution={workflowExecution} />
                        <span className="text-xl font-semibold text-content-primary">{workflowName(workflow)}</span>
                    </div>
                    <Text className="text-base text-content-secondary">#{workflowExecution.id}</Text>
                </div>
            )}
            <ErrorMessage error={workflowExecutionError} />
            {workflowExecution?.status?.failureMessage && (
                <Banner variant="danger" text={workflowExecution.status.failureMessage} />
            )}
            {workflowExecution && <WorkflowExecutionSummary workflowExecution={workflowExecution} />}
            {workflowExecution && actions && (
                <WorkflowExecutionPerformanceChart workflow={workflow} actions={actions} />
            )}
            {actions && workflow && workflowExecutionId && (
                <WorkflowExecutionOutputsTable
                    outputs={outputs || []}
                    actions={actions}
                    workflow={workflow}
                    workflowExecutionId={workflowExecutionId}
                />
            )}
        </div>
    );
};

const ExecutionStatusIconLarge: FC<{ execution: PlainWorkflowExecution }> = ({ execution }) => {
    const phase = execution.status?.phase;

    if (phase === WorkflowExecutionPhase.COMPLETED) {
        return <IconCircleCheckedFilled className="size-6 shrink-0 text-content-success" />;
    }

    if (phase === WorkflowExecutionPhase.RUNNING || phase === WorkflowExecutionPhase.PENDING) {
        return <IconSpinner size="lg" className="shrink-0 animate-spin text-content-muted" />;
    }

    if (phase === WorkflowExecutionPhase.STOPPED || phase === WorkflowExecutionPhase.STOPPING) {
        return <IconWarningCircle size="lg" className="shrink-0 text-content-muted" />;
    }

    if (phase === WorkflowExecutionPhase.DELETED) {
        return <IconTrash size="lg" className="shrink-0 text-content-destructive" />;
    }

    if (phase === WorkflowExecutionPhase.DELETING) {
        return <IconSpinner size="lg" className="shrink-0 animate-spin text-content-muted" />;
    }

    return null;
};
